import typeCheck from 'common/util/typeCheck';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import {
  COMMISSION_TYPE_FLAT_RATE,
  COMMISSION_TYPE_PERCENTAGE,
} from 'common/financeConstants';

import { roundTwoDecimals } from 'common/formatters';

/**
 * Calculates an invoice's due date. The formula is: Lease start date + invoice payout policy (if any).
 *
 * @param  {DateString} options.leaseStartDate      Valid ISOString of the lease start date.
 * @param  {Number}     options.invoicePayoutPolicy The merchant's invoice payout policy.
 * @return {Moment}     Moment instance of the invoice's due date.
 */
export function calculateInvoiceDueDate({ leaseStartDate, invoicePayoutPolicy = 0 }) {
  typeCheck('leaseStartDate::DateString', leaseStartDate);
  typeCheck('invoicePayoutPolicy::Number', invoicePayoutPolicy);
  // TODO(@d-oliveros): Add month offset?
  return momentWithAustinTimezone(leaseStartDate).add(invoicePayoutPolicy, 'days');
}

/**
 * Calculates an apartment locator's commion due date.
 * The formula is: Lease start date + invoice payout policy + month offset.
 *
 * @param  {DateString} options.leaseStartDate      Valid ISOString of the lease start date.
 * @param  {Number}     options.invoicePayoutPolicy The merchant's invoice payout policy.
 * @return {Moment}     Moment instance of the commission's due date.
 */
export function calculateApartmentLocatorCommissionDueDate({ leaseStartDate, invoicePayoutPolicy }) {
  typeCheck('leaseStartDate::DateString', leaseStartDate);
  typeCheck('invoicePayoutPolicy::Number', invoicePayoutPolicy);
  // TODO(@d-oliveros): Add month offset, ask Joel about it.
  return momentWithAustinTimezone(leaseStartDate).add(invoicePayoutPolicy, 'days');
}

/**
 * Calculates a commission's amount.
 *
 * @param  {number} options.rentPrice            The base rent price.
 * @param  {number} options.commissionPercentage The commission's percentage cut.
 * @param  {number} options.commissionFlatRate   The commission's flat rate amount.
 * @return {Object} Object with the resulting amount owed and the commission type.
 */
export function calculateCommissionAmount({ rentPrice, commissionPercentage, commissionFlatRate }) {
  typeCheck('rentPrice::PositiveNumber', rentPrice);
  typeCheck('commissionPercentage::Maybe PositiveNumber', commissionPercentage);
  typeCheck('commissionFlatRate::Maybe PositiveNumber', commissionFlatRate);

  if (!!commissionPercentage === !!commissionFlatRate) { // xor
    const error = new Error(
      '[calculateCommissionAmount] Accepts commissionPercentage or ' +
      'commissionFlatRate, but not both.'
    );
    error.name = 'SrCalculateInvoiceAmountDueInvalidArgumentsError';
    error.details = {
      rentPrice,
      commissionPercentage,
      commissionFlatRate,
    };
    throw error;
  }

  if (commissionFlatRate) {
    return {
      commissionType: COMMISSION_TYPE_FLAT_RATE,
      commissionAmount: commissionFlatRate,
    };
  }
  return {
    commissionType: COMMISSION_TYPE_PERCENTAGE,
    commissionAmount: roundTwoDecimals(rentPrice * commissionPercentage),
  };
}

/**
 * Gets a listing's commission flat rate and percentage out of the listing's compensation package.
 *
 * @param  {Object} listing Listing object.
 * @return {Object}         `null` if the listing has no compensation details, or an object.
 */
export function getListingCommissionDetails(listing) {
  const compensationPackage = listing.compensationPackage
    ? listing.compensationPackage[0]
    : {};
  const { commissionFlatRate, commissionPercentage } = compensationPackage;
  if (commissionPercentage && commissionFlatRate) {
    const error = new Error('Invalid listing compensation package.');
    error.name = 'SrListingInvalidCompensationPackageError';
    error.details = {
      listingId: listing.id,
      compensationPackage: listing.compensationPackage,
    };
    throw error;
  }
  if (!commissionFlatRate && !commissionPercentage) {
    return null;
  }
  return {
    commissionFlatRate,
    commissionPercentage,
  };
}
